import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import Pagination from "../../../components/Pagination";
import SortHeader from "../../../components/SortHeader";
import SelectCompany from "../../../components/selects/SelectCompany";
import { ModalDelete } from "../../../components/ModalDelete";

import api from "../../../services/api";
import { getToken, hasPermission } from "../../../services/auth";
import { getSubdomain } from "../../../helpers/domain";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney } from "../../../helpers/conversions";

import ModalEntry from "../ModalEntry";
import ModalSettle from "../ModalSettle";
import ModalSettleBatch from "../ModalSettleBatch";
import BillingCompanyFilter from "../BillingCompanyFilter";
import { storeBillingCompany, useBillingCompanies } from "../billingCompany";
import { flattenCategories, getCategoryLabel } from "../helpers";

import PeriodNavigator from "../components/PeriodNavigator";
import SearchInPeriod from "../components/SearchInPeriod";
import BankAccountMultiSelect from "../components/BankAccountMultiSelect";
import MoreFilters from "../components/MoreFilters";
import FilterChips from "../components/FilterChips";
import SummaryCards from "../components/SummaryCards";
import BulkActionsBar from "../components/BulkActionsBar";
import EntrySituationBadge from "../components/EntrySituationBadge";
import NewEntryButton from "../components/NewEntryButton";
import ModalTransfer from "../components/ModalTransfer";
import RowActionsMenu from "../components/RowActionsMenu";

import ModalBatchAction from "./ModalBatchAction";
import { buildStatementApiQuery, buildStatementSearch, parseStatementFilters } from "./query";

const TYPE_OPTIONS = [
    { label: 'Receita', value: 'receivable' },
    { label: 'Despesa', value: 'payable' },
    { label: 'Transferência', value: 'transfer' },
];

const SITUATION_OPTIONS = [
    { label: 'Em aberto', value: 'open' },
    { label: 'Atrasado', value: 'overdue' },
    { label: 'Realizado', value: 'realized' },
];

const RECONCILED_OPTIONS = [
    { label: 'Conciliados', value: 'yes' },
    { label: 'Não conciliados', value: 'no' },
];

const ORIGIN_OPTIONS = [
    { label: 'Manual', value: 'manual' },
    { label: 'Pedido', value: 'order' },
    { label: 'Pedido de compra', value: 'purchase_order' },
    { label: 'Nota de entrada', value: 'purchase_invoice' },
];

const indexMap = [
    { index: 'Data', value: 'movement_date' },
    { index: 'Resumo do lançamento', value: 'description' },
    { index: 'Valor (R$)', value: 'signed_amount' },
];

const TOTAL_HELP = 'Receitas (em aberto e realizadas) menos despesas (em aberto e realizadas). Transferências entre contas não entram na conta.';

const labelsOf = (options, values) => values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');

const CompanyFilterEditor = ({ value, setValue, setMeta }) => {
    const [companies, setCompanies] = useState([]);

    const change = id => {
        setValue(id);
        setMeta(companies.find(company => company.id === id)?.trade_name ?? '');
    };

    return <SelectCompany label={`Cliente/Fornecedor`} selectedCompany={value || null} setSelectedCompany={change} changeCompanies={setCompanies} />;
};

export const FinanceStatement = () => {
    const [loading, setLoading] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, perPage: 25, total: 0, hasNext: false, hasPrev: false });

    const [categories, setCategories] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);

    const [selectedIds, setSelectedIds] = useState([]);

    const [entryModal, setEntryModal] = useState(null);
    const [entryToSettle, setEntryToSettle] = useState(null);
    const [entryToCancel, setEntryToCancel] = useState(null);
    const [transferModal, setTransferModal] = useState(null);
    const [transferToDelete, setTransferToDelete] = useState(null);
    const [settleBatchEntries, setSettleBatchEntries] = useState(null);
    const [batchAction, setBatchAction] = useState(null);
    const [working, setWorking] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseStatementFilters(location.search), [location.search]);

    const { billingCompanies, defaultBillingCompanyId } = useBillingCompanies();
    const billingCompanyChecked = useRef(false);

    const canManage = hasPermission('manage_finance');
    const showBalance = filters.sort === 'movement_date';

    useEffect(() => {
        api.get(`/finance-category`).then(res => setCategories(res.data.resources)).catch(() => {});
        api.get(`/finance-cost-center`).then(res => setCostCenters(res.data.resources)).catch(() => {});
        api.get(`/finance-bank-account`).then(res => setBankAccounts(res.data.resources)).catch(() => {});
    }, []);

    // Empresa guardada que não existe mais deixaria o extrato sempre vazio.
    useEffect(() => {
        if (billingCompanyChecked.current || billingCompanies.length === 0) return;
        billingCompanyChecked.current = true;

        const selected = filters.billingCompanyId;
        if (!selected || billingCompanies.some(company => company.id === selected)) return;

        storeBillingCompany('');
        updateUrl({ billingCompanyId: '' });
    }, [billingCompanies]);

    useEffect(() => {
        getRows();
        getSummary();
    }, [location.search]);

    const updateUrl = (changes, resetPage = true) => {
        const next = { ...filters, ...changes, page: resetPage ? 1 : (changes.page || filters.page) };
        const search = buildStatementSearch(next);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getRows = () => {
        setLoading(true);
        setSelectedIds([]);

        api.get(`/finance-statement/paginate?${buildStatementApiQuery(filters)}`).then(res => {
            const resources = res.data.resources;
            setRows(resources.data);
            setPagination({
                currentPage: resources.current_page,
                lastPage: resources.last_page,
                perPage: resources.per_page,
                total: resources.total,
                hasNext: resources.next_page_url !== null,
                hasPrev: resources.prev_page_url !== null,
            });
        }).catch(renderError).then(() => setLoading(false));
    };

    const getSummary = () => {
        setLoadingSummary(true);

        api.get(`/finance-statement/summary?${buildStatementApiQuery(filters, { withPage: false })}`)
            .then(res => setSummary(res.data.resource))
            .catch(renderError)
            .then(() => setLoadingSummary(false));
    };

    const refresh = () => {
        getRows();
        getSummary();
    };

    const openReport = kind => {
        const query = buildStatementApiQuery(filters, { withPage: false });
        window.open(`${process.env.REACT_APP_URL}/finance-statement/${kind}?${query}&_token=${getToken()}&_subdomain=${getSubdomain()}`);
    };

    // A linha traz só o resumo da movimentação; os modais precisam do título
    // inteiro (baixas, rateio, anexos).
    const withEntry = (entryId, callback) => {
        setWorking(true);
        api.get(`/finance-entry/${entryId}`)
            .then(res => callback(res.data.resource))
            .catch(renderError)
            .then(() => setWorking(false));
    };

    const withTransfer = (transferId, callback) => {
        setWorking(true);
        api.get(`/finance-transfer/${transferId}`)
            .then(res => callback(res.data.resource))
            .catch(renderError)
            .then(() => setWorking(false));
    };

    const openEntry = row => withEntry(row.entry_id, entry => setEntryModal({ entry, type: entry.type }));
    const openTransfer = row => withTransfer(row.transfer_id, transfer => setTransferModal({ transfer }));

    const cancelEntry = () => {
        setWorking(true);
        api.patch(`/finance-entry/${entryToCancel.entry_id}/cancel`).then(() => {
            toast.success('Título cancelado');
            setEntryToCancel(null);
            refresh();
        }).catch(renderError).then(() => setWorking(false));
    };

    const deleteTransfer = () => {
        setWorking(true);
        api.delete(`/finance-transfer/${transferToDelete.transfer_id}`).then(() => {
            toast.success('Transferência excluída');
            setTransferToDelete(null);
            refresh();
        }).catch(renderError).then(() => setWorking(false));
    };

    const openSettleBatch = () => {
        setWorking(true);
        Promise.all(selectedIds.map(id => api.get(`/finance-entry/${id}`)))
            .then(responses => setSettleBatchEntries(responses.map(res => res.data.resource)))
            .catch(renderError)
            .then(() => setWorking(false));
    };

    const rowActions = row => {
        if (row.transfer_id) {
            return [
                { text: 'Editar', svg: 'edit', action: () => openTransfer(row) },
                { text: 'Excluir', svg: 'delete', action: () => setTransferToDelete(row) },
            ];
        }

        const settleable = ['open', 'partial'].includes(row.entry_status);

        return [
            { text: 'Editar', svg: 'edit', action: () => openEntry(row) },
            settleable && { text: 'Baixar', svg: 'check', action: () => withEntry(row.entry_id, setEntryToSettle) },
            row.entry_status === 'settled' && { text: 'Ver baixas', svg: 'eye', action: () => withEntry(row.entry_id, setEntryToSettle) },
            row.entry_status === 'open' && { text: 'Cancelar título', svg: 'close', action: () => setEntryToCancel(row) },
        ].filter(Boolean);
    };

    const summaryCell = row => {
        const isTransfer = !!row.transfer_id;
        const categoryLabel = row.categories.length > 1
            ? `Rateio (${row.categories.length})`
            : row.categories[0] ? getCategoryLabel(row.categories[0]) : null;

        return (
            <div className="statement-summary">
                <strong>{row.description || (isTransfer ? 'Transferência' : '—')}</strong>
                <span className="statement-meta">
                    {isTransfer &&
                        <span className="statement-chip">
                            {row.kind === 'transfer_out' ? 'Transferência de saída' : 'Transferência de entrada'}
                        </span>
                    }
                    {isTransfer && row.counter_account &&
                        <span>{row.kind === 'transfer_out' ? 'para' : 'de'} {row.counter_account.name}</span>
                    }
                    {!isTransfer && categoryLabel && <span className="statement-chip">{categoryLabel}</span>}
                    {!isTransfer && row.counterpart_name && <span>{row.counterpart_name}</span>}
                </span>
            </div>
        );
    };

    const header = ['Data', 'Resumo do lançamento', 'Situação', 'Valor (R$)'];
    if (showBalance) header.push(<div className="statement-header-right">Saldo (R$)</div>);
    if (canManage) header.push('');

    const tableRows = rows.map((row, index) => {
        const cells = [
            moment(row.date).format('DD/MM/YYYY'),
            summaryCell(row),
            <EntrySituationBadge situation={row.situation} reconciled={row.reconciled} partial={row.partial} />,
            <span className={`statement-amount${row.amount < 0 ? ' negative' : ''}`}>{convertIntToMoney(row.amount)}</span>,
        ];

        if (showBalance) {
            cells.push(<span className={`statement-amount${row.balance < 0 ? ' negative' : ''}`}>{convertIntToMoney(row.balance)}</span>);
        }

        return {
            data: cells,
            buttons: canManage ? [
                <RowActionsMenu
                    actions={rowActions(row)}
                    dropUp={rows.length > 4 && index >= rows.length - 2}
                />
            ] : undefined,
            action: canManage ? () => (row.transfer_id ? openTransfer(row) : openEntry(row)) : undefined,
            // Selecionar marca o título: as duas linhas de um título com baixa
            // parcial são marcadas juntas, porque o lote age no título.
            id: canManage && row.entry_id ? row.entry_id : null,
        };
    });

    const filterDefinitions = [
        { key: 'type', label: 'Tipo', type: 'options', options: TYPE_OPTIONS },
        { key: 'situation', label: 'Situação', type: 'options', options: SITUATION_OPTIONS },
        { key: 'reconciled', label: 'Conciliação', type: 'select', options: RECONCILED_OPTIONS },
        {
            key: 'category',
            label: 'Categoria',
            type: 'select',
            options: flattenCategories(categories).map(category => ({
                label: `${'  '.repeat(category.depth)}${getCategoryLabel(category)}`,
                value: category.id,
            })),
        },
        { key: 'cost_center', label: 'Centro de custo', type: 'select', options: costCenters.map(costCenter => ({ label: costCenter.name, value: costCenter.id })) },
        {
            key: 'company',
            label: 'Cliente/Fornecedor',
            type: 'custom',
            render: (value, setValue, setMeta) => <CompanyFilterEditor value={value} setValue={setValue} setMeta={setMeta} />,
        },
        { key: 'origin', label: 'Origem do lançamento', type: 'options', options: ORIGIN_OPTIONS },
        { key: 'amount', label: 'Faixa de valor', type: 'money-range' },
    ];

    const filterValues = {
        type: filters.types,
        situation: filters.situations,
        reconciled: filters.reconciled,
        category: filters.categoryId,
        cost_center: filters.costCenterId,
        company: filters.companyId,
        origin: filters.origins,
        amount: { min: filters.amountMin || null, max: filters.amountMax || null },
    };

    const applyFilter = (key, value, meta) => {
        const changes = {
            type: { types: value ?? [] },
            situation: { situations: value ?? [] },
            reconciled: { reconciled: value ?? '' },
            category: { categoryId: value ?? '' },
            cost_center: { costCenterId: value ?? '' },
            company: { companyId: value ?? '', companyLabel: value ? (meta ?? filters.companyLabel) : '' },
            origin: { origins: value ?? [] },
            amount: { amountMin: value?.min ?? '', amountMax: value?.max ?? '' },
        }[key];

        updateUrl(changes);
    };

    const chips = [];
    if (filters.search) chips.push({ key: 'search', label: `Busca: ${filters.search}` });
    if (filters.types.length) chips.push({ key: 'type', label: `Tipo: ${labelsOf(TYPE_OPTIONS, filters.types)}` });
    if (filters.situations.length) chips.push({ key: 'situation', label: `Situação: ${labelsOf(SITUATION_OPTIONS, filters.situations)}` });
    if (filters.reconciled) chips.push({ key: 'reconciled', label: labelsOf(RECONCILED_OPTIONS, [filters.reconciled]) });
    if (filters.categoryId) {
        const category = categories.find(item => item.id === filters.categoryId);
        chips.push({ key: 'category', label: `Categoria: ${category ? getCategoryLabel(category) : '…'}` });
    }
    if (filters.costCenterId) {
        chips.push({ key: 'cost_center', label: `Centro de custo: ${costCenters.find(item => item.id === filters.costCenterId)?.name ?? '…'}` });
    }
    if (filters.companyId) chips.push({ key: 'company', label: `Cliente/Fornecedor: ${filters.companyLabel || 'selecionado'}` });
    if (filters.origins.length) chips.push({ key: 'origin', label: `Origem: ${labelsOf(ORIGIN_OPTIONS, filters.origins)}` });
    if (filters.amountMin || filters.amountMax) {
        const parts = [];
        if (filters.amountMin) parts.push(`de ${convertIntToMoney(filters.amountMin, true)}`);
        if (filters.amountMax) parts.push(`até ${convertIntToMoney(filters.amountMax, true)}`);
        chips.push({ key: 'amount', label: `Valor: ${parts.join(' ')}` });
    }
    if (filters.accountIds.length) chips.push({ key: 'account', label: `Contas: ${filters.accountIds.length}` });

    const removeChip = key => {
        if (key === 'search') return updateUrl({ search: '' });
        if (key === 'account') return updateUrl({ accountIds: [] });
        applyFilter(key, null);
    };

    // Limpar não mexe em período nem empresa de faturamento: são o "onde
    // estou", não um recorte.
    const clearFilters = () => updateUrl({
        search: '', accountIds: [], types: [], situations: [], reconciled: '', categoryId: '', costCenterId: '',
        companyId: '', companyLabel: '', origins: [], amountMin: '', amountMax: '', card: '',
    });

    const visibleAccounts = bankAccounts.filter(account => (
        account.active && (!filters.billingCompanyId || account.billing_company_id === filters.billingCompanyId)
    ));
    const activeAccounts = bankAccounts.filter(account => account.active);

    const cards = [
        { key: 'receivable_open', label: 'Receitas em aberto (R$)', tone: 'income', value: summary?.receivable_open },
        { key: 'receivable_realized', label: 'Receitas realizadas (R$)', tone: 'income', value: summary?.receivable_realized },
        { key: 'payable_open', label: 'Despesas em aberto (R$)', tone: 'expense', value: summary?.payable_open },
        { key: 'payable_realized', label: 'Despesas realizadas (R$)', tone: 'expense', value: summary?.payable_realized },
        { key: null, label: 'Total do período (R$)', tone: 'total', value: summary?.total, help: TOTAL_HELP },
    ];

    const batchActions = [
        { text: 'Quitar', action: openSettleBatch },
        { text: 'Reabrir', action: () => setBatchAction('reopen') },
        { text: 'Alterar data', action: () => setBatchAction('change_due_date') },
        { text: 'Alterar conta', action: () => setBatchAction('change_account') },
    ];

    return (
        <>
            {entryModal &&
                <ModalEntry
                    show={true}
                    close={() => setEntryModal(null)}
                    entry={entryModal.entry}
                    type={entryModal.type}
                    categories={categories}
                    costCenters={costCenters}
                    bankAccounts={activeAccounts}
                    billingCompanies={billingCompanies}
                    defaultBillingCompanyId={filters.billingCompanyId || defaultBillingCompanyId}
                    onSaved={refresh}
                />
            }
            <ModalSettle
                show={entryToSettle !== null}
                close={() => setEntryToSettle(null)}
                entry={entryToSettle}
                bankAccounts={activeAccounts}
                onSaved={refresh}
            />
            <ModalSettleBatch
                show={settleBatchEntries !== null}
                close={() => setSettleBatchEntries(null)}
                entries={settleBatchEntries ?? []}
                bankAccounts={activeAccounts}
                onSaved={refresh}
            />
            <ModalBatchAction
                action={batchAction}
                entryIds={selectedIds}
                bankAccounts={activeAccounts}
                close={() => setBatchAction(null)}
                onSaved={refresh}
            />
            <ModalTransfer
                show={transferModal !== null}
                close={() => setTransferModal(null)}
                transfer={transferModal?.transfer ?? null}
                bankAccounts={activeAccounts}
                onSaved={refresh}
            />
            <ModalDelete
                show={entryToCancel !== null}
                text={<p>Tem certeza que deseja cancelar o título <strong>{entryToCancel?.description}</strong>?</p>}
                action={cancelEntry}
                loading={working}
                close={() => setEntryToCancel(null)}
            />
            <ModalDelete
                show={transferToDelete !== null}
                text={<p>Tem certeza que deseja excluir esta transferência?</p>}
                action={deleteTransfer}
                loading={working}
                close={() => setTransferToDelete(null)}
            />

            <PanelTemplate id="finance-statement">
                <PageHeader
                    left={
                        <BillingCompanyFilter
                            billingCompanies={billingCompanies}
                            value={filters.billingCompanyId}
                            change={billingCompanyId => {
                                storeBillingCompany(billingCompanyId);
                                updateUrl({ billingCompanyId, accountIds: [] });
                            }}
                        />
                    }
                    right={
                        <>
                            <Button type={`secondary`} size={`small`} svg={`download`} text={`Exportar`} action={() => openReport('export')} />
                            <Button type={`secondary`} size={`small`} svg={`printer`} text={`Imprimir`} action={() => openReport('print')} />
                            {canManage &&
                                <NewEntryButton
                                    onReceivable={() => setEntryModal({ entry: null, type: 'receivable' })}
                                    onPayable={() => setEntryModal({ entry: null, type: 'payable' })}
                                    onTransfer={() => setTransferModal({ transfer: null })}
                                />
                            }
                        </>
                    }
                />

                <div className="finance-statement-filters">
                    <div className="statement-filter-field" title="Período">
                        <PeriodNavigator value={filters.period} onChange={period => updateUrl({ period })} />
                    </div>
                    <div className="statement-filter-field">
                        <SearchInPeriod value={filters.search} placeholder={`Pesquisar no período selecionado`} onSearch={search => updateUrl({ search })} />
                    </div>
                    <div className="statement-filter-field" title="Conta">
                        <BankAccountMultiSelect accounts={visibleAccounts} value={filters.accountIds} onChange={accountIds => updateUrl({ accountIds })} />
                    </div>
                    <div className="statement-filter-field">
                        <MoreFilters definitions={filterDefinitions} values={filterValues} onApply={applyFilter} />
                    </div>
                    <FilterChips chips={chips} onRemove={removeChip} onClear={clearFilters} />
                </div>

                <SummaryCards
                    cards={cards}
                    active={filters.card || null}
                    onSelect={card => updateUrl({ card: card ?? '' })}
                    loading={loadingSummary}
                />

                {canManage &&
                    <BulkActionsBar count={selectedIds.length} actions={batchActions} onClear={() => setSelectedIds([])} />
                }

                <Box className={`no-padding with-pagination finance-statement-table`}>
                    <Table
                        header={header}
                        rows={tableRows}
                        loading={loading}
                        checks={canManage}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        sortOrdenationAction={item => {
                            if (typeof item !== 'string') return item;

                            const sortHeader = (
                                <SortHeader
                                    index={item}
                                    ordenationItem={filters.sort}
                                    orientation={filters.orientation}
                                    onSort={(sort, orientation) => updateUrl({ sort, orientation })}
                                    indexMap={indexMap}
                                />
                            );

                            return item === 'Valor (R$)' ? <div className="statement-header-right">{sortHeader}</div> : sortHeader;
                        }}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={pagination.currentPage}
                        lastPage={pagination.lastPage}
                        perPage={pagination.perPage}
                        totalResults={pagination.total}
                        hasNextPage={pagination.hasNext}
                        hasPrevPage={pagination.hasPrev}
                        setCurrentPage={page => updateUrl({ page }, false)}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default FinanceStatement;
